/**
 * atomberg-support-repeatable.js
 *
 * Generic repeatable-row engine for Support page multi-item forms (Register
 * Complaint's product rows, Installation Form's item rows). Binds to every
 * [data-support-repeatable] container:
 *   - [data-support-repeatable-list]     - rows render here
 *   - [data-support-repeatable-template] - a <template> with one row's markup
 *   - [data-support-repeatable-add]      - "+ Add another" button
 *   - data-support-repeatable-max="10"   - max rows (default 10)
 *
 * Rows aren't given `name` attributes — the parent form's own submit handler
 * reads each row's fields directly by data-attribute (there's no native form
 * submission; everything goes to the backend as a JSON payload), so this
 * file only owns add/remove/renumber mechanics plus two opt-in per-row
 * behaviors:
 *
 *   - [data-support-row-file] file input (max files/size per page settings,
 *     default 3 files/2MB each if unset, jpg/jpeg/png/pdf) — read via
 *     FileReader and base64-encoded into a
 *     hidden JSON input ([data-support-row-file-json]) the parent form reads.
 *     Also bound for any standalone [data-support-row-file] outside a
 *     repeatable container (e.g. the B2B form's single invoice upload) —
 *     despite the file's name, this behavior isn't repeatable-specific.
 *   - [data-support-row-serial] input — as soon as the value passes the
 *     input's own pattern (16/17-char alphanumeric or FG barcode), on input
 *     or blur and deduped per serial, calls
 *     GET {api_base_url}/api/support/serial-lookup?serial_number= and
 *     auto-fills [data-support-row-category]/[data-support-row-model]/
 *     [data-support-row-sku] if present, locking the category select.
 *     Clearing the serial re-enables it. Fails silently — this is a
 *     convenience lookup, not a blocking gate. Duplicate serial numbers
 *     across rows in the same container are flagged and block submit via
 *     `data-support-repeatable-invalid` on the container, which the parent
 *     form's submit handler checks.
 *
 * Also fires the Warranty Registration tracking plan's per-product-row
 * events (Clicked_Add_Another_Product, entered_serial_number,
 * entered_model_name) via window.AtombergWarrantyAnalytics.fire (exposed by
 * atomberg-warranty-wizard.js) — gated on isWarrantyContext() since this
 * engine is shared with Complaint/Installation's own unrelated forms.
 */
(function () {
  'use strict';

  var DEFAULT_MAX_FILES = 3;
  var DEFAULT_MAX_FILE_BYTES = 2 * 1024 * 1024;
  var ALLOWED_FILE_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'application/pdf'];
  var DUPLICATE_SERIAL_MESSAGE = 'This serial number is already used in another row.';

  /**
   * Reads per-page file limits from the nearest ancestor carrying them (the
   * page root, e.g. sections/atomberg-support.liquid or
   * sections/atomberg-warranty.liquid) so different pages/sections can set
   * different limits — falls back to the historical 2MB/3-file defaults if
   * absent.
   * @param {HTMLElement} el
   */
  function getFileLimits(el) {
    var maxFilesRoot = el.closest('[data-support-max-files-per-row]');
    var maxSizeRoot = el.closest('[data-support-max-file-size-mb]');
    var maxFilesAttr = maxFilesRoot && maxFilesRoot.getAttribute('data-support-max-files-per-row');
    var maxSizeAttr = maxSizeRoot && maxSizeRoot.getAttribute('data-support-max-file-size-mb');
    var maxFiles = maxFilesAttr ? parseInt(maxFilesAttr, 10) : NaN;
    var maxMb = maxSizeAttr ? parseFloat(maxSizeAttr) : NaN;
    return {
      maxFiles: isNaN(maxFiles) || maxFiles <= 0 ? DEFAULT_MAX_FILES : maxFiles,
      maxBytes: isNaN(maxMb) || maxMb <= 0 ? DEFAULT_MAX_FILE_BYTES : maxMb * 1024 * 1024,
    };
  }

  /** @returns {string} */
  function getApiBaseUrl() {
    var root = document.querySelector('[data-support-api-base-url]');
    return (root && root.getAttribute('data-support-api-base-url')) || '';
  }

  /**
   * This engine is shared with Support's own Register Complaint/Installation
   * forms, which the Warranty tracking plan's per-product events don't apply
   * to — every dispatch below is gated on this so those other forms never
   * fire them.
   * @param {HTMLElement} el
   * @returns {boolean}
   */
  function isWarrantyContext(el) {
    return !!el.closest('[data-warranty-wizard]');
  }

  /**
   * @param {string} name
   * @param {Record<string, any>} payload
   */
  function fireWarranty(name, payload) {
    var w = /** @type {any} */ (window);
    if (w.AtombergWarrantyAnalytics) w.AtombergWarrantyAnalytics.fire(name, payload);
  }

  /**
   * 1-indexed row position + total row count, mirroring the same computation
   * in atomberg-warranty-scan.js (kept local here rather than shared — it's
   * three lines, not worth coupling the two files over).
   * @param {HTMLElement} row
   * @param {HTMLElement} container
   * @returns {{position: number, total: number}}
   */
  function rowPositionContext(row, container) {
    var rows = Array.from(container.querySelectorAll('[data-support-repeatable-row]'));
    var index = rows.indexOf(row);
    return { position: index === -1 ? 1 : index + 1, total: rows.length };
  }

  /** @returns {string} */
  function warrantyPhone() {
    var input = /** @type {HTMLInputElement|null} */ (
      document.querySelector('[data-support-otp-phone]')
    );
    return input ? input.value.trim() : '';
  }

  /**
   * @param {string} apiBaseUrl
   * @param {string} serial
   * @returns {Promise<{status: boolean, product_type?: string, product_category?: string, model?: string, sku?: string}>}
   */
  function lookupSerial(apiBaseUrl, serial) {
    var url = apiBaseUrl + '/api/support/serial-lookup?serial_number=' + encodeURIComponent(serial);
    return fetch(url, { headers: { Accept: 'application/json' } })
      .then(function (r) {
        return r.json();
      })
      .catch(function () {
        return { status: false };
      });
  }

  /**
   * @param {File} file
   * @returns {Promise<{name: string, base64: string, size_bytes: number}>}
   */
  function encodeFile(file) {
    return new Promise(function (resolve, reject) {
      var reader = new FileReader();
      reader.onload = function () {
        resolve({ name: file.name, base64: String(reader.result), size_bytes: file.size });
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  }

  /** @param {HTMLElement} container */
  function renumberRows(container) {
    var list = container.querySelector('[data-support-repeatable-list]');
    if (!list) return;
    var rows = list.querySelectorAll('[data-support-repeatable-row]');
    rows.forEach(function (row, i) {
      var label = row.querySelector('[data-support-repeatable-index]');
      if (label) label.textContent = String(i + 1);
      var removeBtn = /** @type {HTMLElement|null} */ (
        row.querySelector('[data-support-repeatable-remove]')
      );
      if (removeBtn) removeBtn.hidden = rows.length <= 1;
    });
    var addBtn = /** @type {HTMLButtonElement|null} */ (
      container.querySelector('[data-support-repeatable-add]')
    );
    var max = parseInt(container.getAttribute('data-support-repeatable-max') || '10', 10);
    if (addBtn) addBtn.hidden = rows.length >= max;
  }

  // Exposed so atomberg-support-forms.js's "Try Again" reset can fix up row
  // numbering/remove-button/add-button visibility after manually deleting
  // rows down to one, without duplicating this bookkeeping there. Also
  // exposes getFileLimits so forms.js can name the configured max size in a
  // 413 error without re-parsing the data-support-max-file-size-mb attribute
  // itself.
  var w = /** @type {any} */ (window);
  w.AtombergSupport = w.AtombergSupport || {};
  w.AtombergSupport.renumberRepeatable = renumberRows;
  w.AtombergSupport.getFileLimits = getFileLimits;

  /** @param {HTMLElement} row */
  function bindFileInput(row) {
    var fileInputEl = /** @type {HTMLInputElement|null} */ (
      row.querySelector('[data-support-row-file]')
    );
    var jsonInputEl = /** @type {HTMLInputElement|null} */ (
      row.querySelector('[data-support-row-file-json]')
    );
    var errorEl = /** @type {HTMLElement|null} */ (
      row.querySelector('[data-support-row-file-error]')
    );
    if (!fileInputEl || !jsonInputEl) return;
    if (/** @type {any} */ (fileInputEl)._supportFileBound) return;
    /** @type {any} */ (fileInputEl)._supportFileBound = true;
    var fileInput = fileInputEl;
    var jsonInput = jsonInputEl;

    fileInput.addEventListener('change', function () {
      var files = Array.from(fileInput.files || []);
      if (errorEl) errorEl.hidden = true;
      var limits = getFileLimits(fileInput);

      if (files.length > limits.maxFiles) {
        if (errorEl) {
          errorEl.textContent = 'You can select upto ' + limits.maxFiles + ' files at a time';
          errorEl.hidden = false;
        }
        fileInput.value = '';
        jsonInput.value = '';
        return;
      }
      var invalid = files.find(function (f) {
        return f.size > limits.maxBytes || ALLOWED_FILE_TYPES.indexOf(f.type) === -1;
      });
      if (invalid) {
        if (errorEl) {
          errorEl.textContent =
            'Please Select a file smaller than ' +
            Math.round(limits.maxBytes / (1024 * 1024)) +
            'MB and of type PDF, JPEG, JPG and PNG';
          errorEl.hidden = false;
        }
        fileInput.value = '';
        jsonInput.value = '';
        return;
      }

      Promise.all(files.map(encodeFile)).then(function (encoded) {
        jsonInput.value = JSON.stringify(encoded);
      });
    });
  }

  /**
   * Applies the serial-lookup's category to the row's select. The backend
   * resolves categories from product-master's own vocabulary ("Ceiling
   * Fan"), which need not equal this theme's Salesforce-label option values
   * ("Ceiling Fans") — so match via data-category-override (the exact
   * product-master string, seeded for this gap) or normalized comparison
   * (same normalization the backend itself uses), and lock the select ONLY
   * on a real match: blind assignment left a disabled EMPTY required select
   * whenever the strings differed.
   * @param {HTMLSelectElement} categorySelect
   * @param {string} rawCategory
   */
  function applyLookupCategory(categorySelect, rawCategory) {
    /** @param {string} s */
    var normalize = function (s) {
      return String(s)
        .toLowerCase()
        .replace(/[^a-z0-9]/g, '');
    };
    var target = normalize(rawCategory);
    var matched = '';
    Array.from(categorySelect.options).forEach(function (opt) {
      if (matched || !opt.value) return;
      var override = opt.getAttribute('data-category-override') || '';
      if ((override && normalize(override) === target) || normalize(opt.value) === target) {
        matched = opt.value;
      }
    });
    if (!matched) return;
    categorySelect.value = matched;
    categorySelect.disabled = true;
  }

  /**
   * @param {HTMLElement} row
   * @param {HTMLElement} container
   */
  function bindSerialLookup(row, container) {
    var serialInputEl = /** @type {HTMLInputElement|null} */ (
      row.querySelector('[data-support-row-serial]')
    );
    if (!serialInputEl) return;
    var serialInput = serialInputEl;
    var categorySelect = /** @type {HTMLSelectElement|null} */ (
      row.querySelector('[data-support-row-category]')
    );
    var modelInput = /** @type {HTMLInputElement|null} */ (
      row.querySelector('[data-support-row-model]')
    );
    var skuInput = /** @type {HTMLInputElement|null} */ (
      row.querySelector('[data-support-row-sku]')
    );

    // Lookup only when the input's own pattern passes — a partial serial can
    // never match a real unit, so hitting the API for it is pure waste.
    // Deduped by value: fires as soon as a typed/pasted serial becomes valid
    // (input) with blur as a fallback, without double-calling for the same
    // serial on the input→blur sequence.
    var lastLookedUpSerial = '';
    function maybeLookup() {
      var serial = serialInput.value.trim();
      if (!serial || !serialInput.checkValidity()) return;
      if (serial === lastLookedUpSerial) return;
      var apiBaseUrl = getApiBaseUrl();
      if (!apiBaseUrl) return;
      lastLookedUpSerial = serial;

      lookupSerial(apiBaseUrl, serial).then(function (res) {
        // Value may have changed while the request was in flight — never
        // apply a stale unit's category/model/sku to the current serial.
        if (serialInput.value.trim() !== serial) return;
        if (!res.status) return;
        if (categorySelect && res.product_category) {
          applyLookupCategory(categorySelect, res.product_category);
        }
        if (modelInput && res.model) {
          modelInput.value = res.model;
          // Direct assignment fires no native event of its own — the
          // manual-entry path below is a separate blur listener, so this is
          // the only place that can report the Autopopulate case.
          if (isWarrantyContext(row)) {
            var ctx = rowPositionContext(row, container);
            fireWarranty('entered_model_name', {
              input_method: 'Autopopulate',
              phone_number: warrantyPhone(),
              product_position: ctx.position,
              total_products_in_form: ctx.total,
              serial_number: serial,
              model_name: res.model,
            });
          }
        }
        if (skuInput && res.sku) skuInput.value = res.sku;
      });
    }

    serialInput.addEventListener('input', function () {
      if (serialInput.value.trim() === '' && categorySelect) {
        categorySelect.disabled = false;
      }
      // Live re-check — without this a corrected serial keeps wearing the
      // stale format error from its last blur, so valid rows look rejected.
      var serialErrorEl = /** @type {HTMLElement|null} */ (
        row.querySelector('[data-support-row-serial-error]')
      );
      if (serialErrorEl && !serialErrorEl.hidden && serialInput.checkValidity()) {
        serialErrorEl.hidden = true;
        serialErrorEl.textContent = '';
      }
      checkDuplicateSerials(container);
      maybeLookup();
    });

    serialInput.addEventListener('blur', function () {
      var serial = serialInput.value.trim();
      // Inline format feedback — the step gate's generic "fill all required
      // fields" doesn't tell the user WHICH field is wrong.
      var serialErrorEl = /** @type {HTMLElement|null} */ (
        row.querySelector('[data-support-row-serial-error]')
      );
      if (serialErrorEl && serial && !serialInput.checkValidity()) {
        serialErrorEl.textContent = serialInput.title || 'Invalid serial/barcode number';
        serialErrorEl.hidden = false;
      }
      if (serial && isWarrantyContext(row)) {
        // Set by atomberg-warranty-scan.js right before it dispatches the
        // synthetic input/blur that lands here — cleared immediately after
        // reading so a later manual edit of the same field reports Manual.
        var scanned = row.getAttribute('data-support-row-serial-source') === 'scanned';
        row.removeAttribute('data-support-row-serial-source');
        var ctx = rowPositionContext(row, container);
        fireWarranty('entered_serial_number', {
          input_method: scanned ? 'Scanned' : 'Manual',
          phone_number: warrantyPhone(),
          product_position: ctx.position,
          total_products_in_form: ctx.total,
          serial_number: serial,
        });
      }
      maybeLookup();
    });

    if (modelInput) {
      var modelInputEl = modelInput;
      modelInputEl.addEventListener('blur', function () {
        var value = modelInputEl.value.trim();
        if (!value || !isWarrantyContext(row)) return;
        var ctx = rowPositionContext(row, container);
        fireWarranty('entered_model_name', {
          input_method: 'Manual',
          phone_number: warrantyPhone(),
          product_position: ctx.position,
          total_products_in_form: ctx.total,
          serial_number: serialInput.value.trim(),
          model_name: value,
        });
      });
    }
  }

  /** @param {HTMLElement} container */
  function checkDuplicateSerials(container) {
    var list = container.querySelector('[data-support-repeatable-list]');
    if (!list) return;
    var serialInputs = /** @type {NodeListOf<HTMLInputElement>} */ (
      list.querySelectorAll('[data-support-row-serial]')
    );
    /** @type {Record<string, number>} */
    var counts = {};
    serialInputs.forEach(function (input) {
      var v = input.value.trim();
      if (!v) return;
      counts[v] = (counts[v] || 0) + 1;
    });
    var hasDuplicate = Object.keys(counts).some(function (v) {
      return (counts[v] || 0) > 1;
    });
    serialInputs.forEach(function (input) {
      var v = input.value.trim();
      var errorEl = /** @type {HTMLElement|null} */ (
        input
          .closest('[data-support-repeatable-row]')
          ?.querySelector('[data-support-row-serial-error]')
      );
      var isDup = v && (counts[v] || 0) > 1;
      // Only own the error element while showing/clearing a DUPLICATE message —
      // blindly rewriting it here was wiping other rows' format errors every
      // time any serial input fired.
      if (errorEl && isDup) {
        errorEl.textContent = DUPLICATE_SERIAL_MESSAGE;
        errorEl.hidden = false;
      } else if (errorEl && errorEl.textContent === DUPLICATE_SERIAL_MESSAGE) {
        errorEl.textContent = '';
        errorEl.hidden = true;
      }
    });
    container.setAttribute('data-support-repeatable-invalid', hasDuplicate ? 'true' : 'false');
  }

  /** @param {HTMLElement} container */
  function addRow(container) {
    var template = /** @type {HTMLTemplateElement|null} */ (
      container.querySelector('[data-support-repeatable-template]')
    );
    var list = container.querySelector('[data-support-repeatable-list]');
    if (!template || !list) return;
    var fragment = /** @type {DocumentFragment} */ (template.content.cloneNode(true));
    var row = /** @type {HTMLElement|null} */ (fragment.firstElementChild);
    if (!row) return;
    list.appendChild(row);
    bindFileInput(row);
    bindSerialLookup(row, container);
    renumberRows(container);
    // Each row is its own [data-support-scope] for product-type-scoped
    // category/issue selects — filter it to the type select's default value.
    var w = /** @type {any} */ (window);
    if (w.AtombergSupport && row.hasAttribute('data-support-scope')) {
      w.AtombergSupport.filterScopedSelects(row);
    }
    if (w.AtombergSupport && w.AtombergSupport.initModelSearch) {
      w.AtombergSupport.initModelSearch(row);
    }
    // Required markers are decorated at form init, which this row postdates.
    if (w.AtombergSupport && w.AtombergSupport.markRequiredLabels) {
      w.AtombergSupport.markRequiredLabels(row);
    }
  }

  /** @param {HTMLElement} container */
  function init(container) {
    if (/** @type {any} */ (container)._supportRepeatableBound) return;
    /** @type {any} */ (container)._supportRepeatableBound = true;

    var addBtn = container.querySelector('[data-support-repeatable-add]');
    if (addBtn) {
      addBtn.addEventListener('click', function () {
        var max = parseInt(container.getAttribute('data-support-repeatable-max') || '10', 10);
        var list = container.querySelector('[data-support-repeatable-list]');
        var count = list ? list.querySelectorAll('[data-support-repeatable-row]').length : 0;
        if (count >= max) return;
        addRow(container);
        if (isWarrantyContext(container)) {
          var newCount = list
            ? list.querySelectorAll('[data-support-repeatable-row]').length
            : count + 1;
          fireWarranty('Clicked_Add_Another_Product', {
            phone_number: warrantyPhone(),
            product_position: newCount,
            total_products_in_form: newCount,
          });
        }
      });
    }

    var listEl = container.querySelector('[data-support-repeatable-list]');
    if (listEl) {
      var list = listEl;
      list.addEventListener('click', function (e) {
        var target = /** @type {HTMLElement} */ (e.target);
        var removeBtn =
          target && target.closest ? target.closest('[data-support-repeatable-remove]') : null;
        if (!removeBtn) return;
        var row = removeBtn.closest('[data-support-repeatable-row]');
        var rows = list.querySelectorAll('[data-support-repeatable-row]');
        if (row && rows.length > 1) {
          row.remove();
          renumberRows(container);
          checkDuplicateSerials(container);
        }
      });
    }

    // Start with exactly one row.
    var existing = listEl ? listEl.querySelectorAll('[data-support-repeatable-row]').length : 0;
    if (existing === 0) addRow(container);
    else renumberRows(container);
  }

  function initAll() {
    document.querySelectorAll('[data-support-repeatable]').forEach(function (el) {
      init(/** @type {HTMLElement} */ (el));
    });
    // Standalone file inputs not managed by the repeatable engine — e.g. the
    // B2B form's single invoice upload, which uses a static (uncloned)
    // [data-support-repeatable-row] purely so collectItemRows() picks it up
    // as one item, but was never rendered via addRow(). Only skip rows that
    // are actually inside a [data-support-repeatable] container, since those
    // were already bound when addRow() cloned them — bindFileInput no-ops if
    // already bound, so this is safe to run broadly.
    document.querySelectorAll('[data-support-row-file]').forEach(function (el) {
      var input = /** @type {HTMLElement} */ (el);
      if (input.closest('[data-support-repeatable]')) return; // handled via addRow
      var wrapper = input.closest('.atomberg-support-field') || input.parentElement;
      if (wrapper) bindFileInput(/** @type {HTMLElement} */ (wrapper));
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }
})();
